/* Compositor-driven motion: no autoplay interval, per-frame JS or scroll handler. */
if (!customElements.get('rd-trust-carousel')) {
  customElements.define('rd-trust-carousel', class extends HTMLElement {
    connectedCallback() {
      this.focused = false;
      this.track = this.querySelector('.rd-trust__grid');
      this.viewport = this.querySelector('.rd-trust__viewport');
      this.items = [...this.track.children].filter(item => !item.hasAttribute('data-clone'));
      this.mobile = matchMedia('(max-width: 749px)');
      this.events = new AbortController();
      const on = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: this.events.signal });
      on(this.mobile, 'change', () => this.setup());
      on(document, 'visibilitychange', () => this.updatePlayback());
      on(this.viewport, 'pointerdown', event => this.startDrag(event));
      on(this.viewport, 'pointermove', event => this.moveDrag(event), { passive: true });
      on(this.viewport, 'pointerup', event => this.endDrag(event));
      on(this.viewport, 'pointercancel', event => this.endDrag(event));
      on(this.viewport, 'lostpointercapture', event => this.endDrag(event));
      on(this.viewport, 'keydown', event => {
        if (!this.animation || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.animation.pause();
        this.seek(this.phase() + (event.key === 'ArrowRight' ? 1 : -1) * this.width / this.items.length);
        this.updatePlayback();
      });
      on(this.viewport, 'focusin', () => { this.focused = this.viewport.matches(':focus-visible'); this.updatePlayback(); });
      on(this.viewport, 'focusout', () => { this.focused = false; this.updatePlayback(); });
      this.visibility = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      });
      this.visibility.observe(this);
      this.resize = new ResizeObserver(() => this.setup());
      this.resize.observe(this.viewport);
      this.setup();
    }
    setup() {
      const width = this.viewport.clientWidth * this.items.length / 2;
      if (this.drag && (!this.mobile.matches || this.width !== width)) this.endDrag();
      if (!this.mobile.matches || this.items.length < 3) {
        this.drag = null; cancelAnimationFrame(this.dragFrame); this.dragFrame = 0;
        this.animation?.cancel(); this.animation = null;
        this.track.querySelectorAll('[data-clone]').forEach(item => item.remove());
        this.removeAttribute('data-ready');
        this.width = 0;
        return;
      }
      if (this.animation && this.width === width) return;
      this.drag = null; cancelAnimationFrame(this.dragFrame); this.dragFrame = 0;
      const fraction = this.width ? this.phase() / this.width : 0;
      this.animation?.cancel();
      this.track.querySelectorAll('[data-clone]').forEach(item => item.remove());
      const clone = item => {
        const copy = item.cloneNode(true);
        copy.setAttribute('data-clone', ''); copy.setAttribute('aria-hidden', 'true'); copy.inert = true;
        copy.removeAttribute('data-shopify-editor-block');
        copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        return copy;
      };
      this.track.prepend(...this.items.map(clone));
      this.track.append(...this.items.map(clone));
      this.width = width;
      this.duration = width / 12 * 1000; // A gentle 12 CSS pixels per second to the left.
      this.animation = this.track.animate([
        { transform: `translate3d(${-width}px, 0, 0)` },
        { transform: `translate3d(${-2 * width}px, 0, 0)` }
      ], { duration: this.duration, iterations: Infinity, easing: 'linear' });
      this.animation.pause(); this.seek(fraction * width);
      this.setAttribute('data-ready', '');
      const box = this.getBoundingClientRect();
      this.visible = box.bottom > 0 && box.top < innerHeight;
      this.updatePlayback();
    }
    phase() { return this.animation && this.duration ? ((this.animation.currentTime || 0) % this.duration) / this.duration * this.width : 0; }
    seek(phase) { this.animation.currentTime = ((phase % this.width + this.width) % this.width) / this.width * this.duration; }
    startDrag(event) {
      if (!this.animation || !event.isPrimary || event.button !== 0) return;
      this.pendingPhase = undefined;
      this.drag = { id: event.pointerId, x: event.clientX, phase: this.phase() };
      this.focused = false;
      this.animation.pause();
      this.viewport.setPointerCapture(event.pointerId);
    }
    moveDrag(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      this.pendingPhase = this.drag.phase - (event.clientX - this.drag.x);
      if (!this.dragFrame) this.dragFrame = requestAnimationFrame(() => {
        this.dragFrame = 0;
        if (this.animation) this.seek(this.pendingPhase);
      });
    }
    endDrag(event) {
      if (!this.drag || (event && event.pointerId !== this.drag.id)) return;
      const id = this.drag.id;
      if (event?.type === 'pointerup') {
        this.pendingPhase = this.drag.phase - (event.clientX - this.drag.x);
      }
      cancelAnimationFrame(this.dragFrame); this.dragFrame = 0;
      if (this.pendingPhase !== undefined) this.seek(this.pendingPhase);
      this.pendingPhase = undefined;
      this.drag = null;
      if (this.viewport.hasPointerCapture(id)) this.viewport.releasePointerCapture(id);
      this.updatePlayback();
    }
    updatePlayback() {
      if (!this.animation) return;
      const paused = this.drag || this.focused || !this.visible || document.hidden;
      if (paused) this.animation.pause(); else this.animation.play();
    }
    disconnectedCallback() {
      this.events?.abort(); this.resize?.disconnect(); this.visibility?.disconnect();
      this.animation?.cancel(); this.animation = null;
      cancelAnimationFrame(this.dragFrame);
      this.track?.querySelectorAll('[data-clone]').forEach(item => item.remove());
      this.removeAttribute('data-ready');
    }
  });
}
